import { Button } from '@zep-us/zus';
import { useTranslation } from 'next-i18next';
type ValidDifficulty = 0 | 1 | 2 | 3 | 4;

// i18n key (`ai.difficulty.{0~4}`) 가 strict literal union 으로 타이핑되어 있어
// 단순 `number` 로는 좁힐 수 없다. type predicate 로 0~4 정수만 통과시켜야
// 이후 `as const` 가 정확한 5-튜플 union 으로 추론된다.
const isValidDifficulty = (difficulty: number | undefined): difficulty is ValidDifficulty => {
  return difficulty !== undefined && Number.isInteger(difficulty) && difficulty >= 0 && difficulty <= 4;
};

/**
 * DifficultyBadge - 결과 카드 헤더에 표시되는 난이도 chip.
 *
 * Figma `Button/outlined/neutral` (size xs) variant 1:1 매핑 — zus Button 을
 * 그대로 사용해 디자인 시스템 변경 시 자동 추종된다. 클릭 동작이 필요 없으므로
 * `asChild` 로 `<span>` 을 렌더해 의미상 비-interactive 표시 요소로 둔다.
 *
 * 서버 fan-out 이 각 question 에 stamp 한 difficulty (0~4) 를 그대로 i18n key
 * `ai.difficulty.{n}` 로 매핑한다. 옵션 dropdown (BuildOption.tsx 의
 * SelectAiQuizLevel) 과 정확히 같은 i18n key 를 공유하므로 Option ↔ Display
 * 텍스트가 자동 동기화된다 — emoji prefix 도 i18n 문자열에 인라인되어 있다.
 *
 * 0~4 범위 밖이거나 누락된 값(undefined) 은 안전하게 null 을 반환한다 —
 * legacy 응답 / 부분 fan-out 회귀로부터 UI 를 보호한다.
 */
export const DifficultyBadge = ({
  difficulty,
  className
}: {
  difficulty: number | undefined;
  className?: string;
}) => {
  const {
    t
  } = useTranslation(['build']);
  if (!isValidDifficulty(difficulty)) {
    return null;
  }
  return <Button asChild variant="outlined" intent="neutral" size="xs" className={className} data-sentry-element="Button" data-sentry-component="DifficultyBadge" data-sentry-source-file="DifficultyBadge.tsx">
      <span>{t(`ai.difficulty.${difficulty}` as const)}</span>
    </Button>;
};